import React, { useState, useEffect, Fragment, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { websocketService } from '../services/websocketService';
import SystemMotdDialog from './SystemMotdDialog';
import versionInfo from '../version.json';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  List as ListIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Science as ScienceIcon,
  AdminPanelSettings as AdminIcon,
  VpnKey as ApiKeyIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  People as UsersIcon,
  FolderSpecial as ProjectsIcon,
  Public as PublicIcon,
  CloudDownload as FilesIcon,
  HealthAndSafety as IntegrityIcon,
  Backup as BackupIcon,
  Business as OrganizationIcon,
  Speed as SystemStatusIcon,
  TrendingUp as TrendingUpIcon,
  Storage as StorageIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  Article as LogsIcon,
  History as HistoryIcon,
  HelpOutline as HelpOutlineIcon,
  SystemUpdateAlt as PluginReleasesIcon,
  Tune as CalibrationIcon,
  MenuBook as ApiDocsIcon,
  Hub as ControlCenterIcon,
  SupportAgent as ServiceCenterIcon,
  Announcement as AnnouncementIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApiAccess } from '../hooks/useApiAccess';
import { getAdminInterfaceConfig } from '../utils/userCapabilities';
import { apiService } from '../services/apiService';
import ObservatoryCustomIcon from './icons/ObservatoryIcon';
import { getAccessToken } from '../services/authStorage';

const drawerWidth = 240;

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Standalone mode strips the drawer, app bar, and footer — used when
  // embedding a legacy admin page inside a floating iframe window.
  const isStandalone = new URLSearchParams(location.search).has('standalone');
  const { user, logout } = useAuth();
  const apiAccessAllowed = useApiAccess();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [serverVersion, setServerVersion] = useState('');
  const [adminSectionOpen, setAdminSectionOpen] = useState(true);
  const [reportingSectionOpen, setReportingSectionOpen] = useState(true);
  const [administrativeSectionOpen, setAdministrativeSectionOpen] = useState(true);
  const [memberships, setMemberships] = useState([]);
  const [orgMemberships, setOrgMemberships] = useState([]);
  const [isProjectOwner, setIsProjectOwner] = useState(false);
  const [firstObservatoryId, setFirstObservatoryId] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Initialize WebSocket connection and time update
  useEffect(() => {
    try {
      if (!websocketService.isConnected) {
        websocketService.connect();
      }
    } catch (error) {
      console.error('Error initializing WebSocket connection:', error);
    }

    // Fetch server version directly from health endpoint
    const fetchServerVersion = async () => {
      try {
        // Fetch Python scheduler version from proxied health endpoint
        const response = await fetch('/api/v1/scheduler/health');
        if (response.ok) {
          const data = await response.json();
          // Python scheduler returns version directly in data object
          if (data?.data?.version) {
            // Strip unhelpful commit info like "(unknown)" or "(dev)"
            let version = data.data.version;
            version = version.replace(/\s*\((unknown|dev)\)$/i, '');
            setServerVersion(version);
          }
        }
      } catch (error) {
        console.error('Error fetching server version:', error);
      }
    };
    fetchServerVersion();

    // Set up event listeners. Named (not inline) so the cleanup below can
    // remove them — the singleton websocketService keeps its listener arrays
    // for the whole session, so anonymous handlers here would accumulate on
    // every layout remount.
    const handleObservationUpdate = (update) => {
      // You can dispatch events or update state here
    };
    const handleNotification = (notification) => {
      // Show notifications to user
    };
    websocketService.onObservationUpdate(handleObservationUpdate);
    websocketService.onNotification(handleNotification);

    // Update time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      // Don't disconnect — WebSocket is a singleton shared across layouts
      clearInterval(timeInterval);
      websocketService.offObservationUpdate(handleObservationUpdate);
      websocketService.off('notification', handleNotification);
    };
  }, []);

  // Fetch first observatory ID for Mission Control link
  useEffect(() => {
    const fetchFirstObservatory = async () => {
      try {
        const response = await apiService.getObservatories({ limit: 1 });
        const obs = response.data?.observatories || response.data || [];
        const first = Array.isArray(obs) ? obs[0] : null;
        if (first) setFirstObservatoryId(first._id || first.id);
      } catch (e) { /* ignore */ }
    };
    fetchFirstObservatory();
  }, []);

  // Load user memberships for observatory admin capabilities
  useEffect(() => {
    const loadUserMemberships = async () => {
      if (user?._id || user?.id) {
        try {
          const userId = user._id || user.id;
          const response = await apiService.getUserMemberships(userId);
          if (response?.success) {
            setMemberships(response.data || []);
          }
        } catch (error) {
          console.error('Error loading user memberships:', error);
          setMemberships([]);
        }

        // Load organization memberships
        let userOrgMemberships = [];
        try {
          const orgResponse = await apiService.getUserOrganizations();
          userOrgMemberships = orgResponse.data?.organizations || orgResponse.data || [];
          setOrgMemberships(userOrgMemberships);
        } catch (error) {
          console.error('Error loading org memberships:', error);
          setOrgMemberships([]);
        }

        // Check if user owns any projects (directly or via owned orgs)
        try {
          const userId = user._id || user.id;
          // getAllProjects walks every page so a truly-owned project past the
          // first page can't yield a false "owns nothing" (flat array result).
          const projResponse = await apiService.getAllProjects();
          const projects = Array.isArray(projResponse) ? projResponse : [];
          const ownedOrgIds = userOrgMemberships
            .filter(org => org.is_owner)
            .map(org => org.organization?._id || org._id);
          const ownsProjects = projects.some(p => {
            const ownerId = p.owner_id?._id || p.owner_id;
            return (p.owner_type === 'user' && ownerId === userId) ||
                   (p.owner_type === 'organization' && ownedOrgIds.includes(ownerId));
          });
          setIsProjectOwner(ownsProjects);
        } catch (error) {
          setIsProjectOwner(false);
        }
      } else {
        setMemberships([]);
        setOrgMemberships([]);
        setIsProjectOwner(false);
      }
    };

    loadUserMemberships();
  }, [user]);

  // Check if user has admin role (server_admin has highest privileges)
  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';

  // Get admin interface configuration
  const adminConfig = getAdminInterfaceConfig(user, memberships, orgMemberships);

  // Base menu items for all users
  const userMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Submit Observation', icon: <AddIcon />, path: '/observations/new' },
    { text: 'Search Observations', icon: <SearchIcon />, path: '/observations/search' },
    { text: 'Observatories', icon: <ObservatoryCustomIcon />, path: '/observatories' },
    { text: 'My Observations', icon: <ListIcon />, path: '/observations' },
    { text: 'My Files', icon: <FilesIcon />, path: '/files' },
    { text: 'My Projects', icon: <ProjectsIcon />, path: '/projects' },
    { text: 'My Activities', icon: <HistoryIcon />, path: '/activity-history' },
    { text: 'My Usage', icon: <TrendingUpIcon />, path: '/my-usage' },
    { text: 'Target Library', icon: <StarIcon />, path: '/library' },
    { text: 'Frame Library', icon: <CalibrationIcon />, path: '/calibration/library' },
    { text: 'Exoplanet Transits', icon: <PublicIcon />, path: '/exoplanets' },
    { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
    { text: 'Target Report', icon: <StarIcon />, path: '/reports/targets' },
    { text: 'Contact Support', icon: <HelpOutlineIcon />, path: '/support' },
    // API Documentation is hidden when the user's subscription tier doesn't grant API access.
    ...(apiAccessAllowed ? [{ text: 'API Documentation', icon: <ApiDocsIcon />, path: '/api/docs', external: true }] : []),
  ];

  // Administrative menu items (for observatory admins and owners — includes Admin Dashboard)
  const administrativeMenuItems = [
    { text: 'Admin Dashboard', icon: <AdminIcon />, path: '/admin/dashboard' },
    { text: 'My Observatories', icon: <ObservatoryCustomIcon />, path: '/administrative/observatories' },
    ...(adminConfig.isOrgAdmin ? [
      // isOrgAdmin = owner OR can_manage_members/observatories, matching the
      // AdminRoute guard — an org admin (not just the owner) needs this entry to
      // reach org management (e.g. to add members).
      { text: 'My Organizations', icon: <OrganizationIcon />, path: '/administrative/organizations' },
    ] : []),
    ...(isProjectOwner ? [
      { text: 'My Projects', icon: <ProjectsIcon />, path: '/administrative/projects' },
    ] : []),
    { text: 'Users', icon: <UsersIcon />, path: '/administrative/users' },
    { text: 'Observations', icon: <ScienceIcon />, path: '/administrative/observations' },
    { text: 'Announcements', icon: <AnnouncementIcon />, path: '/admin/announcements' },
    { text: 'Log Viewer', icon: <LogsIcon />, path: '/admin/logs' },
  ];

  // Full admin menu items (server admins only)
  const serverAdminOnlyItems = [
    { text: 'Control Center', icon: <ControlCenterIcon />, path: '/admin/control-center' },
    { text: 'Service Center', icon: <ServiceCenterIcon />, path: '/admin/service-center' },
    { text: 'Admin Dashboard', icon: <AdminIcon />, path: '/admin/dashboard' },
    { text: 'System Announcements', icon: <AnnouncementIcon />, path: '/admin/announcements' },
    { text: 'Observatories', icon: <ObservatoryCustomIcon />, path: '/admin/observatories' },
    { text: 'Observations', icon: <ListIcon />, path: '/admin/observations' },
    { text: 'Organizations', icon: <OrganizationIcon />, path: '/admin/organizations' },
    { text: 'Projects', icon: <ProjectsIcon />, path: '/admin/projects' },
    { text: 'Users', icon: <UsersIcon />, path: '/admin/users' },
    { text: 'API Keys', icon: <ApiKeyIcon />, path: '/admin/api-keys' },
    { text: 'Database Integrity', icon: <IntegrityIcon />, path: '/admin/integrity' },
    { text: 'Backup Management', icon: <BackupIcon />, path: '/admin/backups' },
    { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics' },
    { text: 'System Settings', icon: <SettingsIcon />, path: '/admin/settings' },
    { text: 'System Status', icon: <SystemStatusIcon />, path: '/admin/status' },
    { text: 'Log Viewer', icon: <LogsIcon />, path: '/admin/logs' },
    { text: 'Log Settings', icon: <StorageIcon />, path: '/admin/logs/settings' },
    { text: 'Plugin Releases', icon: <PluginReleasesIcon />, path: '/admin/plugin-releases' },
    { text: 'Calibration Library', icon: <CalibrationIcon />, path: '/admin/calibration/library' },
  ];

  const adminMenuItems = serverAdminOnlyItems;

  // Billing & Reports menu items
  const billingMenuItems = [
    { text: 'Usage Summary', icon: <TrendingUpIcon />, path: '/admin/billing/dashboard' },
    { text: 'Usage Explorer', icon: <SearchIcon />, path: '/admin/billing/explorer' },
    { text: 'User Usage', icon: <UsersIcon />, path: '/admin/billing/users' },
    { text: 'Project Usage', icon: <ProjectsIcon />, path: '/admin/billing/projects' },
    { text: 'Observatory Utilization', icon: <StorageIcon />, path: '/admin/billing/observatories' },
  ];

  const drawer = (
    <Box>
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }}
          onClick={() => navigate('/about')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate('/about'); }}
        >
          <ScienceIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" noWrap>
            Asterism
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      
      {/* User sections */}
      <List>
        {userMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={!item.external && (location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path + '/')))}
              onClick={() => {
                if (item.external) {
                  const token = getAccessToken();
                  const url = token ? `${item.path}?token=${token}` : item.path;
                  window.open(url, '_blank', 'noopener');
                } else {
                  navigate(item.path);
                }
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Observatory Administrative section - only show if user has observatory admin capabilities */}
      {adminConfig.showAdministrativeMenu && (
        <>
          <Divider sx={{ my: 1 }} />

          {/* Observatory Administration Section */}
          <ListItem disablePadding>
            <ListItemButton onClick={() => setAdministrativeSectionOpen(!administrativeSectionOpen)}>
              <ListItemText
                primary={
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    ADMINISTRATION
                  </Typography>
                }
              />
              {administrativeSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
          </ListItem>
          <Collapse in={administrativeSectionOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {administrativeMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}

      {/* Full Administration section - server admins only */}
      {adminConfig.isFullAdmin && (
        <>
          <Divider sx={{ my: 1 }} />

          {/* Administration Section */}
          <ListItem disablePadding>
            <ListItemButton onClick={() => setAdminSectionOpen(!adminSectionOpen)}>
              <ListItemText
                primary={
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    ADMINISTRATION
                  </Typography>
                }
              />
              {adminSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
          </ListItem>
          <Collapse in={adminSectionOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {adminMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>

        </>
      )}

      {/* Reporting section - visible to server admins and observatory admins */}
      {(adminConfig.showAdminMenu || adminConfig.showAdministrativeMenu) && (
        <>
          <Divider sx={{ my: 1 }} />
          <ListItem disablePadding>
            <ListItemButton onClick={() => setReportingSectionOpen(!reportingSectionOpen)}>
              <ListItemText
                primary={
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    REPORTING
                  </Typography>
                }
              />
              {reportingSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
          </ListItem>
          <Collapse in={reportingSectionOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {billingMenuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton
                    sx={{ pl: 4 }}
                    selected={location.pathname === item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}
    </Box>
  );

  if (isStandalone) {
    return (
      <Box sx={{ minHeight: '100vh', p: 2, backgroundColor: 'background.default' }}>
        <SystemMotdDialog />
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}><CircularProgress /></Box>}>
          <Outlet />
        </Suspense>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          
          {/* UTC Time Display */}
          <Typography 
            variant="body2" 
            sx={{ 
              mr: 2, 
              fontFamily: 'monospace',
              color: 'rgba(255, 255, 255, 0.9)'
            }}
          >
            UTC {currentTime.toISOString().substring(11, 19)}
          </Typography>
          
          {/* User role indicator */}
          {isAdmin && (
            <Chip
              label="Admin"
              size="small"
              color="secondary"
              sx={{ mr: 2 }}
            />
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">
              {user?.name || user?.email}
            </Typography>
            <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.[0] || user?.email?.[0] || 'U'}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { navigate('/profile'); handleUserMenuClose(); }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            {firstObservatoryId && (
              <MenuItem onClick={() => { navigate(`/observatory/${firstObservatoryId}/operations`); handleUserMenuClose(); }}>
                <ListItemIcon>
                  <ObservatoryCustomIcon fontSize="small" />
                </ListItemIcon>
                Operations View
              </MenuItem>
            )}
            {/* Admin view switches — Control Center for any admin who can reach it
                (AdminRoute admits observatory/org admins too), Service Center for
                server admins only (ServerAdminRoute). */}
            {adminConfig.showAdminMenu && (
              <MenuItem onClick={() => { navigate('/admin/control-center'); handleUserMenuClose(); }}>
                <ListItemIcon>
                  <ControlCenterIcon fontSize="small" />
                </ListItemIcon>
                Admin Control Center
              </MenuItem>
            )}
            {adminConfig.isFullAdmin && (
              <MenuItem onClick={() => { navigate('/admin/service-center'); handleUserMenuClose(); }}>
                <ListItemIcon>
                  <ServiceCenterIcon fontSize="small" />
                </ListItemIcon>
                Service Center
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          mb: 6, // Add margin for footer
        }}
      >
        <SystemMotdDialog />
        <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}><CircularProgress /></Box>}>
          <Outlet />
        </Suspense>
      </Box>

      {/* Version Footer */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { sm: drawerWidth },
          right: 0,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          p: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          zIndex: 1100,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          GUI v{versionInfo.version} ({versionInfo.gitCommit})
        </Typography>
        {serverVersion && (
          <>
            <Typography variant="caption" color="text.secondary">•</Typography>
            <Typography variant="caption" color="text.secondary">
              Scheduler v{serverVersion}
            </Typography>
          </>
        )}
        <Typography variant="caption" color="text.secondary">•</Typography>
        <Typography variant="caption" color="text.secondary">
          © 2025-2026 First Light Observatory Systems LLC
        </Typography>
      </Box>
    </Box>
  );
};

export default Layout;